import { r as createLogger } from "../logger-BU8YqMGU.js";
import { registerFrameUpdate } from "../ar/frame-loop.js";
import { t as registerSessionDisposer } from "../session-disposers-I5HJZq-T.js";
import { clampedAlpha } from "./lerp-utils.js";
import { createTextSprite } from "./text-sprite.js";
import { computeTargetPlacement } from "./wayfinding-placement.js";
import * as THREE from "three";
//#region ../src/visualization/wayfinding-hud.ts
/**
* Wayfinding HUD presenter — frustum-locked target indicators as camera
* children.
*
* Framework graduation of the field-validated Prototype-2 `ARWayfindingHUD`
* (AR_Wayfinding_HUD_Component/Task 2, PR #194), per
* `GpsPlusSlamJs_Docs/docs/2026-07-17-0756-wayfinding-hud-framework-graduation-plan.md`.
* Each target gets an arrow/circle indicator pair plus a distance label,
* driven per frame by the pure placement seam (`wayfinding-placement.ts`).
*
* Key deltas vs. the prototype (all recorded in the plan's decisions):
* - Targets come from a single `getTargets()` callback polled each frame —
*   no imperative setWaypoints/addWaypoint/removeWaypoint API. Since the
*   2026-07-20 per-target config plan the callback returns
*   `WayfindingTarget[]` (position + optional id / per-target deadband /
*   inactive-arrow opt-in); per-target state is keyed by `id ?? index`.
* - The camera is an explicit required option and is NEVER reparented
*   (the prototype's `scene.add(camera)` would destroy the framework's
*   arWorldGroup → basisChangeNode → arpose → camera alignment chain).
*   Indicators are added TO the camera; the camera is not moved.
* - No renderer handle: placement always reads the projection matrix
*   (exact for any symmetric-frustum perspective camera, and in-session
*   the matrix is the only truthful source — fov/aspect are stale there).
* - Self-registers with the frame loop and the session-disposer registry,
*   so `resetWebXRState()` tears it down even if the app drops the handle.
*/
const log = createLogger("WayfindingHud");
/** Defaults for the optional {@link WayfindingHudOptions} fields. */
const DEFAULT_WAYFINDING_HUD = {
	hudDistance: 2.5,
	indicatorScale: 1,
	labelScale: 1
};
/** Indicator tint used by the procedural cone/ring fallbacks. */
const HUD_COLOR = 16726832;
/**
* Damping rate for the circle's snap-then-damp smoothing, consumed as
* `clampedAlpha(CIRCLE_DAMPING_RATE, dt)` (lerp-utils idiom) so the damping
* speed is frame-rate independent. 9 reproduces the field-validated
* prototype's fixed 0.15-per-frame factor at 60 fps.
*/
const CIRCLE_DAMPING_RATE = 9;
function assertPositiveFiniteOption(name, value) {
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) throw new RangeError(`createWayfindingHud: ${name} must be a positive finite number, got ${value}`);
}
function validateHudRefs(options) {
	if (!options.camera?.isPerspectiveCamera) throw new TypeError("createWayfindingHud: camera must be a THREE.PerspectiveCamera (the ar module getCamera() result)");
	if (typeof options.getTargets !== "function") throw new TypeError("createWayfindingHud: getTargets must be a function returning WayfindingTarget[]");
}
/** The required deadband mirrors the prototype's strict constructor contract. */
function validateHudDeadband(distanceMin, distanceMax) {
	if (typeof distanceMin !== "number" || !Number.isFinite(distanceMin) || distanceMin < 0) throw new RangeError(`createWayfindingHud: distanceMin must be a non-negative finite number, got ${distanceMin}`);
	if (typeof distanceMax !== "number" || !Number.isFinite(distanceMax) || distanceMax < distanceMin) throw new RangeError(`createWayfindingHud: distanceMax must be finite and ≥ distanceMin (${distanceMin}), got ${distanceMax}`);
}
/**
* Validate a {@link WayfindingHudOptions} object. Throws `TypeError` /
* `RangeError` on malformed input.
*/
function validateWayfindingHudOptions(options) {
	validateHudRefs(options);
	validateHudDeadband(options.distanceMin, options.distanceMax);
	assertPositiveFiniteOption("hudDistance", options.hudDistance ?? DEFAULT_WAYFINDING_HUD.hudDistance);
	assertPositiveFiniteOption("indicatorScale", options.indicatorScale ?? DEFAULT_WAYFINDING_HUD.indicatorScale);
	assertPositiveFiniteOption("labelScale", options.labelScale ?? DEFAULT_WAYFINDING_HUD.labelScale);
}
function resolveTexture(source) {
	if (source === void 0) return null;
	if (typeof source === "string") return {
		texture: new THREE.TextureLoader().load(source),
		owned: true
	};
	return {
		texture: source,
		owned: false
	};
}
function isVector3(value) {
	return typeof value === "object" && value !== null && value.isVector3 === true;
}
/** Same `0 ≤ min ≤ max` (finite) rule the placement seam enforces per call. */
function isValidDeadband(min, max) {
	return typeof min === "number" && Number.isFinite(min) && min >= 0 && typeof max === "number" && Number.isFinite(max) && max >= min;
}
/** Shape triage for one raw getTargets() element; null = shape is fine. */
function checkTargetShape(raw) {
	if (isVector3(raw)) return "legacy";
	if (typeof raw !== "object" || raw === null) return "invalid";
	if (!isVector3(raw.position)) return "invalid";
	if (raw.id !== void 0 && typeof raw.id !== "string") return "invalid";
	return null;
}
const SHAPE_ISSUE_MESSAGES = {
	legacy: "getTargets() returned a plain THREE.Vector3 at index %i — the API takes WayfindingTarget objects now; wrap it as { position: vector }. Hiding it.",
	invalid: "getTargets() element at index %i is not a WayfindingTarget ({ position: THREE.Vector3, id?: string, … }). Hiding it."
};
/**
* Create the wayfinding HUD and start driving it via the frame loop.
*
* Follows the `createGpsCompassCubes` / `enableArWorldGroupAlignment`
* idiom: factory + handle, frame-loop registration at construction,
* self-disposal on session teardown.
*/
function createWayfindingHud(options) {
	validateWayfindingHudOptions(options);
	const { camera, getTargets, distanceMin, distanceMax } = options;
	const hudDistance = options.hudDistance ?? DEFAULT_WAYFINDING_HUD.hudDistance;
	const indicatorScale = options.indicatorScale ?? DEFAULT_WAYFINDING_HUD.indicatorScale;
	const labelScale = options.labelScale ?? DEFAULT_WAYFINDING_HUD.labelScale;
	const arrowTexture = resolveTexture(options.arrowSprite);
	const circleTexture = resolveTexture(options.circleSprite);
	let arrowGeometry = null;
	let circleGeometry = null;
	let hudMaterial = null;
	const states = /* @__PURE__ */ new Map();
	let warnedBadTargets = false;
	/**
	* One-shot bookkeeping for consumer bugs surfaced at the readTargets
	* boundary: `<reason>:<key>` → already logged. Entries are cleared when
	* the offending target heals, so a later regression logs again instead of
	* staying silent forever.
	*/
	const loggedIssues = /* @__PURE__ */ new Set();
	function logIssueOnce(issueKey, message) {
		if (loggedIssues.has(issueKey)) return;
		loggedIssues.add(issueKey);
		log.error(`createWayfindingHud: ${message}`);
	}
	function getHudMaterial() {
		hudMaterial ??= new THREE.MeshBasicMaterial({
			color: HUD_COLOR,
			depthTest: false,
			depthWrite: false,
			transparent: true
		});
		return hudMaterial;
	}
	function makeIndicatorSprite(texture) {
		const material = new THREE.SpriteMaterial({
			map: texture,
			color: 16777215,
			depthTest: false,
			depthWrite: false,
			transparent: true
		});
		const sprite = new THREE.Sprite(material);
		sprite.renderOrder = 999;
		sprite.scale.set(.3 * indicatorScale, .3 * indicatorScale, 1);
		sprite.visible = false;
		return sprite;
	}
	function makeArrow() {
		if (arrowTexture) return makeIndicatorSprite(arrowTexture.texture);
		if (!arrowGeometry) {
			arrowGeometry = new THREE.ConeGeometry(.1 * indicatorScale, .3 * indicatorScale, 16);
			arrowGeometry.translate(0, .15 * indicatorScale, 0);
		}
		const mesh = new THREE.Mesh(arrowGeometry, getHudMaterial());
		mesh.renderOrder = 999;
		mesh.visible = false;
		return mesh;
	}
	function makeCircle() {
		if (circleTexture) return makeIndicatorSprite(circleTexture.texture);
		circleGeometry ??= new THREE.RingGeometry(.08 * indicatorScale, .12 * indicatorScale, 32);
		const mesh = new THREE.Mesh(circleGeometry, getHudMaterial());
		mesh.renderOrder = 999;
		mesh.visible = false;
		return mesh;
	}
	function makeLabel() {
		const scale = hudDistance * labelScale;
		const label = createTextSprite({
			canvasWidth: 256,
			canvasHeight: 128,
			font: "bold 48px Arial, sans-serif",
			background: "pill",
			depthWrite: false,
			transparent: true,
			linearFilters: true,
			renderOrder: 1e3,
			scale: {
				x: .16 * scale,
				y: .08 * scale,
				z: 1
			}
		});
		label.sprite.visible = false;
		return label;
	}
	function makeState() {
		const arrow = makeArrow();
		arrow.name = "wayfinding-arrow";
		const circle = makeCircle();
		circle.name = "wayfinding-circle";
		const label = makeLabel();
		label.sprite.name = "wayfinding-label";
		camera.add(arrow);
		camera.add(circle);
		camera.add(label.sprite);
		return {
			currentState: null,
			arrow,
			circle,
			label,
			smoothedCirclePos: new THREE.Vector3()
		};
	}
	/** Detach one indicator and release its PER-TARGET resources only —
	* shared procedural geometry/material stay alive for the other targets. */
	function disposeIndicator(indicator) {
		camera.remove(indicator);
		if (indicator.isSprite) indicator.material.dispose();
	}
	function disposeState(state) {
		disposeIndicator(state.arrow);
		disposeIndicator(state.circle);
		camera.remove(state.label.sprite);
		state.label.dispose();
	}
	/** Create states for new keys and dispose states whose key vanished from
	* the (validated) target list — getter-API replacement for the prototype's
	* addWaypoint/removeWaypoint mutations, see the sidecar. */
	function syncTargetStates(resolved) {
		const liveKeys = new Set(resolved.map((target) => target.key));
		for (const [key, state] of states) if (!liveKeys.has(key)) {
			disposeState(state);
			states.delete(key);
		}
		for (const target of resolved) if (!states.has(target.key)) states.set(target.key, makeState());
	}
	function hideAll(state) {
		state.arrow.visible = false;
		state.circle.visible = false;
		state.label.sprite.visible = false;
	}
	function showCircle(state, placement, previous, dt) {
		state.arrow.visible = false;
		state.circle.visible = true;
		if (previous !== "circle") state.smoothedCirclePos.copy(placement.circlePosition);
		else state.smoothedCirclePos.lerp(placement.circlePosition, clampedAlpha(CIRCLE_DAMPING_RATE, dt));
		state.circle.position.copy(state.smoothedCirclePos);
	}
	function positionArrow(state, arrowPosition, arrowRotationZ) {
		state.circle.visible = false;
		state.arrow.visible = true;
		state.arrow.position.copy(arrowPosition);
		if (state.arrow.isSprite) state.arrow.material.rotation = arrowRotationZ;
		else state.arrow.rotation.set(0, 0, arrowRotationZ);
	}
	function showArrow(state, placement) {
		positionArrow(state, placement.arrowPosition, placement.arrowRotationZ);
	}
	/** The per-target parity opt-in: draw the display-only inactive arrow (the
	* hysteresis state stays 'hidden' — see the seam sidecar's no-bypass
	* invariant), with the label gated by `showLabelWhenInactive`. */
	function showInactiveArrow(state, payload, distanceLabel, showLabel) {
		positionArrow(state, payload.arrowPosition, payload.arrowRotationZ);
		if (!showLabel) {
			state.label.sprite.visible = false;
			return;
		}
		state.label.setText(distanceLabel);
		state.label.sprite.position.copy(payload.labelPosition);
		state.label.sprite.visible = true;
	}
	function updateTarget(target, state, dt) {
		const placement = computeTargetPlacement({
			targetWorldPos: target.position,
			camera,
			hudDistance,
			distanceMin: target.distanceMin,
			distanceMax: target.distanceMax,
			showArrowWhenInactive: target.showArrowWhenInactive,
			...state.currentState !== null ? { previousState: state.currentState } : {},
			isXrSession: true
		});
		const previous = state.currentState;
		state.currentState = placement.state;
		if (placement.state === "hidden") {
			if (placement.inactiveArrow) showInactiveArrow(state, placement.inactiveArrow, placement.distanceLabel, target.showLabelWhenInactive);
			else hideAll(state);
			return;
		}
		state.label.setText(placement.distanceLabel);
		state.label.sprite.position.copy(placement.labelPosition);
		state.label.sprite.visible = true;
		if (placement.state === "circle") {
			showCircle(state, placement, previous, dt);
			return;
		}
		showArrow(state, placement);
	}
	/** Defensive boundary: a getter returning garbage counts as "no targets"
	* (logged once) so the frame loop and scene state stay consistent. */
	function readTargets() {
		const targets = getTargets();
		if (Array.isArray(targets)) return targets;
		if (!warnedBadTargets) {
			warnedBadTargets = true;
			log.error("getTargets() did not return an array; treating as empty target list", targets);
		}
		return [];
	}
	/** Validate the per-target deadband with the seam's `0 ≤ min ≤ max` rule.
	* Returns null (target hidden, one log) instead of throwing — the getter
	* is polled per frame, so a throw would spam the frame loop and kill
	* manual-update hosts. */
	function resolveDeadband(target, key) {
		const min = target.distanceMin ?? distanceMin;
		const max = target.distanceMax ?? distanceMax;
		const issueKey = `deadband:${String(key)}`;
		if (!isValidDeadband(min, max)) {
			logIssueOnce(issueKey, `target "${String(key)}" must satisfy 0 ≤ distanceMin ≤ distanceMax (finite), got distanceMin=${String(min)}, distanceMax=${String(max)}. Hiding it.`);
			return null;
		}
		loggedIssues.delete(issueKey);
		return {
			min,
			max
		};
	}
	/** Boundary triage for one raw element → ResolvedTarget, or null when the
	* element must be hidden (shape/duplicate/deadband issue, logged once). */
	function resolveTarget(raw, index, seenIds, duplicateIds) {
		const shapeIssue = checkTargetShape(raw);
		if (shapeIssue) {
			logIssueOnce(`${shapeIssue}:${index}`, SHAPE_ISSUE_MESSAGES[shapeIssue].replace("%i", String(index)));
			return null;
		}
		loggedIssues.delete(`legacy:${index}`);
		loggedIssues.delete(`invalid:${index}`);
		if (raw.id !== void 0) {
			if (seenIds.has(raw.id)) {
				duplicateIds.add(raw.id);
				logIssueOnce(`duplicate:${raw.id}`, `duplicate target id "${raw.id}" in one getTargets() result — only the first occurrence is shown.`);
				return null;
			}
			seenIds.add(raw.id);
		}
		const key = raw.id ?? index;
		const deadband = resolveDeadband(raw, key);
		if (!deadband) return null;
		return {
			key,
			position: raw.position,
			distanceMin: deadband.min,
			distanceMax: deadband.max,
			showArrowWhenInactive: raw.showArrowWhenInactive ?? false,
			showLabelWhenInactive: raw.showLabelWhenInactive ?? true
		};
	}
	/** A duplicate-id log entry is keyed by id, not index, so it is cleared
	* only once the duplication actually disappears from the result (clearing
	* it while the duplicate persists would re-log every frame). */
	function clearHealedDuplicates(duplicateIds) {
		for (const issue of loggedIssues) if (issue.startsWith("duplicate:") && !duplicateIds.has(issue.slice(10))) loggedIssues.delete(issue);
	}
	function resolveTargets(raw) {
		const seenIds = /* @__PURE__ */ new Set();
		const duplicateIds = /* @__PURE__ */ new Set();
		const resolved = [];
		raw.forEach((target, index) => {
			const result = resolveTarget(target, index, seenIds, duplicateIds);
			if (result) resolved.push(result);
		});
		clearHealedDuplicates(duplicateIds);
		return resolved;
	}
	function update(dt) {
		const resolved = resolveTargets(readTargets());
		syncTargetStates(resolved);
		for (const target of resolved) updateTarget(target, states.get(target.key), dt);
	}
	/** Release the target-shared procedural resources and any owned textures. */
	function releaseSharedResources() {
		arrowGeometry?.dispose();
		circleGeometry?.dispose();
		hudMaterial?.dispose();
		if (arrowTexture?.owned) arrowTexture.texture.dispose();
		if (circleTexture?.owned) circleTexture.texture.dispose();
	}
	const unregister = options.autoRegisterFrameUpdate ?? true ? registerFrameUpdate(update) : null;
	let disposed = false;
	const handle = {
		update(dt) {
			if (disposed) return;
			update(dt);
		},
		dispose() {
			if (disposed) return;
			disposed = true;
			unregister?.();
			for (const state of states.values()) disposeState(state);
			states.clear();
			releaseSharedResources();
			deregisterSessionDisposer();
		}
	};
	const deregisterSessionDisposer = registerSessionDisposer(() => handle.dispose());
	return handle;
}
//#endregion
export { DEFAULT_WAYFINDING_HUD, createWayfindingHud, validateWayfindingHudOptions };
